import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import type { PurchaseOrder } from "../types/purchase-order.types";
import {
  PurchaseOrderStatus,
  purchaseOrderCanCancel,
  purchaseOrderCanConfirm,
} from "../constants/purchase-order.constants";

export type PurchaseOrderActionsDropdownProps = {
  record: PurchaseOrder;
  canRead: boolean;
  canUpdate: boolean;
  canConfirm: boolean;
  canCancel: boolean;
  onConfirm: (record: PurchaseOrder) => void;
  onCancel: (record: PurchaseOrder) => void;
};

export default function PurchaseOrderActionsDropdown({
  record,
  canRead,
  canUpdate,
  canConfirm,
  canCancel,
  onConfirm,
  onCancel,
}: PurchaseOrderActionsDropdownProps) {
  const st = record.status;
  const showEdit = canUpdate && st === PurchaseOrderStatus.DRAFT;
  const showConfirm = canConfirm && purchaseOrderCanConfirm(st);
  const showCancel = canCancel && purchaseOrderCanCancel(st);
  const hasMenuItems = canRead || showEdit || showConfirm || showCancel;

  if (!hasMenuItems) {
    return <span className="text-muted small">—</span>;
  }

  const hasItemsBeforeCancel = canRead || showEdit || showConfirm;

  return (
    <UncontrolledDropdown>
      <DropdownToggle
        type="button"
        className="btn btn-soft-secondary btn-sm"
        tag="button"
        aria-label="Purchase order actions"
      >
        <i className="ri-more-2-fill align-middle" />
      </DropdownToggle>
      <DropdownMenu container="body" end>
        {canRead ? (
          <DropdownItem
            tag={Link}
            to={`/inventory-management/purchase-orders/${record.id}`}
          >
            <i className="ri-eye-line align-bottom me-2 text-muted" />
            View
          </DropdownItem>
        ) : null}
        {showEdit ? (
          <DropdownItem
            tag={Link}
            to={`/inventory-management/purchase-orders/edit/${record.id}`}
          >
            <i className="ri-pencil-line align-bottom me-2 text-muted" />
            Edit
          </DropdownItem>
        ) : null}
        {showConfirm ? (
          <DropdownItem onClick={() => onConfirm(record)}>
            <i className="ri-check-double-line align-bottom me-2 text-muted" />
            Confirm
          </DropdownItem>
        ) : null}
        {showCancel ? (
          <>
            {hasItemsBeforeCancel ? <DropdownItem divider /> : null}
            <DropdownItem className="text-danger" onClick={() => onCancel(record)}>
              <i className="ri-close-circle-line align-bottom me-2" />
              Cancel
            </DropdownItem>
          </>
        ) : null}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}
